import React, { useState } from "react";
import client from "../lib/api";
import { tr } from "../i18n/LangContext";
import { useLocations } from "../hooks/useLocations";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Eye, EyeOff, Pencil, Plus, Check } from "lucide-react";
import { toast } from "sonner";

const Row = ({ kind, item, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(item.name);
  const patch = async (body) => {
    try { await client.patch(`/locations/${kind}/${item.id}`, body); onSaved(); setEdit(false); } catch (e) { toast.error("Couldn't save"); }
  };
  return (
    <div data-testid={`loc-${item.id}`} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${item.hidden ? "opacity-50" : ""}`}>
      {edit ? <><Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`loc-rename-${item.id}`} className="h-8 bg-input/60 border-white/10 text-sm" /><button onClick={() => patch({ name })} data-testid={`loc-save-${item.id}`} className="text-brand-green"><Check className="h-4 w-4" /></button></>
        : <span className="flex-1 text-sm text-white">{item.name}</span>}
      {!edit && <button onClick={() => setEdit(true)} data-testid={`loc-edit-${item.id}`} className="text-muted-foreground hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>}
      <button onClick={() => patch({ hidden: !item.hidden })} data-testid={`loc-hide-${item.id}`} title={item.hidden ? "Show" : "Hide"} className="text-muted-foreground hover:text-white">{item.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
    </div>
  );
};

const Adder = ({ kind, zoneId, onSaved, placeholder }) => {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await client.post(`/locations/${kind}`, { name, zone_id: zoneId }); setName(""); onSaved(); toast.success("Added"); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add"); }
  };
  return <div className="mt-1 flex gap-2"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} data-testid={`loc-add-${kind}-${zoneId || "root"}`} className="h-8 bg-input/60 border-white/10 text-sm" /><Button onClick={add} size="sm" data-testid={`loc-add-btn-${kind}-${zoneId || "root"}`} className="h-8 bg-white/10 text-white hover:bg-white/20"><Plus className="h-4 w-4" /></Button></div>;
};

export const LocationsPanel = () => {
  const loc = useLocations();
  const refresh = () => loc.refresh();
  return (
    <div className="mt-4 grid gap-6 lg:grid-cols-2" data-testid="locations-panel">
      <div>
        <h3 className="font-display text-base font-semibold text-white">{tr("Medina zones & quarters (riads)")}</h3>
        <p className="text-xs text-muted-foreground">{tr("Rename or hide without touching existing properties — they keep their quarter ID.")}</p>
        <div className="mt-3 space-y-4">
          {loc.zones.map((z) => (
            <div key={z.id} className="rounded-2xl border border-white/[0.07] bg-card/40 p-3">
              <Row kind="zones" item={z} onSaved={refresh} />
              <div className="ml-4 border-l border-white/[0.06] pl-2">{z.quarters.map((q) => <Row key={q.id} kind="quarters" item={q} onSaved={refresh} />)}<Adder kind="quarters" zoneId={z.id} onSaved={refresh} placeholder="New quarter in this zone" /></div>
            </div>
          ))}
          <Adder kind="zones" onSaved={refresh} placeholder="New zone" />
        </div>
      </div>
      <div>
        <h3 className="font-display text-base font-semibold text-white">Areas (villas, apartments, land)</h3>
        <div className="mt-3 rounded-2xl border border-white/[0.07] bg-card/40 p-3">{loc.areas.map((a) => <Row key={a.id} kind="areas" item={a} onSaved={refresh} />)}<Adder kind="areas" onSaved={refresh} placeholder="New area, e.g. Route de Fès" /></div>
      </div>
    </div>
  );
};
